import { Cart, Discount, Order } from '../cart';
import { WithSDKError } from '../shared';
import {
  CreatePayPalOrderPayload,
  CreatePayPalOrderResponse,
  ValidatePayPalCheckoutPayload,
} from './PayPal';
import {
  FinalizeStripeCheckoutPayload,
  ReleaseStripeCheckoutAfterFailurePayload,
  ValidateStripeCheckoutPayload,
  StripePaymentResponse,
  GetPayInContextPayload,
  PayInContext,
  GetPayInContextResponse,
  EligiblePaymentMethodType,
  MarketplaceFxQuotePayload,
} from './Stripe';

export {
  type CreatePayPalOrderPayload,
  type CreatePayPalOrderResponse,
  type ValidatePayPalCheckoutPayload,
};

export {
  type FinalizeStripeCheckoutPayload,
  type ReleaseStripeCheckoutAfterFailurePayload,
  type ValidateStripeCheckoutPayload,
  type StripePaymentResponse,
  type GetPayInContextPayload,
  type PayInContext,
  type GetPayInContextResponse,
  type EligiblePaymentMethodType,
  type MarketplaceFxQuotePayload,
};

export type CartResponse = WithSDKError<Cart>;

export type PrepareCartPayload = {
  discountCode?: string;
  variant: { skus: string[] };
  shouldThrowOnEnrollmentOrEntitlementError?: boolean;
};

export type PrepareCartForProgramPayload = {
  programId: string;
  discountCode?: string;
  shouldThrowOnEnrollmentOrEntitlementError?: boolean;
};

export type PrepareCartResponse = WithSDKError<{
  discountErrorCode?: AlertCode;
  cart: Cart;
}>;

export type RedeemDiscountPayload = {
  cartID: string;
  code: string;
};

export type RedeemDiscountResponse = WithSDKError<{
  code: string;
  cart: Cart;
}>;

export type RemoveDiscountPayload = {
  cartID: string;
  discount: Discount;
};

export type RemoveDiscountResponse = WithSDKError<{
  cart: Cart;
}>;

export type RemoveLineItemPayload = {
  cartID: string;
  lineItemId: string;
};

export type RemoveLineItemResponse = WithSDKError<{
  cart: Cart;
}>;

export type ClearCartResponse = WithSDKError<{
  cart: Cart;
}>;

export type PriceDetails = {
  lineItemsTotalPrice?: number;
  cartTotal?: number;
  currencyCode?: string;
  fractionDigits?: number;
  cartDiscount: {
    discount?: Discount;
    amount?: number;
    percent?: string;
  };
  discountInDollars: number;
  lineItemsTaxed: {
    amount: {
      centAmount: number;
    };
    grossAmount: {
      centAmount: number;
    };
  };
};

export enum AlertCode {
  CART_CHANGED = 'cart_changed',
  CART_LOAD_FAILED = 'cart_load_failed',
  FALLBACK = 'fallback',
  FALLBACK_WITH_RELOAD_INFORMATION = 'fallback_with_reload_information',
  PAYMENT_PROCESSING_FAILED = 'payment_processing_failed',
  ORDER_PROCESSING_FAILED = 'order_processing_failed',
  PRODUCT_NOT_FOUND = 'product_not_found',
  PRODUCT_NOT_FOUND_FOR_COUPON = 'product_not_found_for_coupon',
  PRODUCT_ENTITLEMENTS_ENROLLEMENT_FETCH_ERROR = 'product_entitlements_enrollment_fetch_error',
  PRODUCT_USER_ME_FETCH_ERROR = 'product_user_me_fetch_error',
  PRODUCT_USER_ACCOUNT_PROFILE_FETCH_ERROR = 'product_user_account_profile_fetch_error',
  PRODUCT_CREDIT_ELIGIBILITY_ERROR = 'product_credit_eligibility_error',
  PRODUCT_UNAVAILABLE = 'product_unavailable',
  PRODUCT_UNAVAILABLE_TO_BUY = 'product_unavailable_to_buy',
  PRODUCT_ALREADY_ENROLLED = 'product_already_enrolled',
  PRODUCT_ALREADY_ENTITLED = 'product_already_entitled',
  ORDER_ALREADY_PLACED = 'order_already_placed',
  REDEEM_DISCOUNT = 'redeem_discount',
  REMOVE_DISCOUNT = 'remove_discount',
  SKU_NOT_PROVIDED = 'sku_not_provided',
  TRANSACTION_DECLINED = 'transaction_declined',
  PAYPAL_PAYMENT_PROCESSING_FAILED = 'paypal_payment_processing_failed',
  PAYPAL_TRANSACTION_CANCELLED = 'paypal_transaction_cancelled',
  COUPON_ALREADY_USED = 'coupon_already_used',
  COUPON_ALREADY_USED_BY_USER = 'coupon_already_used_by_user',
  COUPON_NOT_ELIGIBLE_FOR_CART = 'coupon_not_eligible_for_cart',
  COUPON_NOT_ELIGIBLE_FOR_BUNDLED_PURCHASE = 'coupon_not_eligible_for_bundled_purchase',
  COUPON_NOT_FOUND = 'coupon_not_found',
  COUPON_NOT_VALID_FOR_CART = 'coupon_not_valid_for_cart',
  DISCOUNT_CODE_EXPIRED = 'discount_code_expired',
  UNKNOWN_ERROR = 'order_processing_failed',
  DISCOUNT_CODE_NOT_PROVIDED = 'discount_code_not_provided',
  AUTO_PROMO_APPLIED = 'auto_promo_applied',
  ITEM_REMOVED = 'item_removed',
  CURRENCY_SELECTED = 'currency_selected',
  LOCKED_RATE = 'locked_rate',
  SDN_HIT = 'sdn_hit',
  AUTOFILL_USER_INFORMATION = 'autofill_user_information',
  QUOTE_EXPIRED = 'quote_expired',
  RATES_UNAVAILABLE = 'rates_unavailable',
  GEO_DISCOUNT_APPLIED = 'geo_discount_applied',
  GEO_COUNTRY_MISMATCH = 'geo_country_mismatch',
  ERROR_APPLYING_GEO_COUPON = 'error_applying_geo_coupon',
  GEO_COURSE_INELIGIBLE = 'geo_course_ineligible',
  GEO_PROGRAM_INELIGIBLE = 'geo_program_ineligible',
  GEO_DISCOUNT_APPLY_FAILED = 'geo_discount_apply_failed',
  UPI_PAYMENT_FAILED = 'upi_payment_failed',
}

export enum PSP {
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
}

export enum UNKNOWN_PSP {
  Type = 'Unknown',
}

export enum COUNTRY {
  UNDEFINED = 'UNDEFINED',
}

export type ValidateCheckoutResponse = WithSDKError<{
  ctPaymentID: string;
  payerName: string;
  /** Present on successful Stripe validate after definitive SDN + PI create. */
  clientSecret?: string;
  /** Present on successful Stripe validate; signed resume JWT. */
  resumeToken?: string;
}>;

export type ProcessCheckoutPayload = {
  ctPaymentID: string;
  cartID: string;
  payerName: string;
};

export type ProcessCheckoutResponse = WithSDKError<{
  order: Order | null;
}>;

export type RedeemDiscountResult = {
  isError: boolean;
  discounts: Discount[] | null;
  errorCode?: string;
};
